import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate, Link } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import PasswordInput, { passwordScore } from '@/components/PasswordInput';
import icone from '@/assets/icone.png';

const ResetPassword = () => {
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [ready, setReady] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    let active = true;

    const prepare = async () => {
      const url = new URL(window.location.href);
      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const code = url.searchParams.get('code');
      const tokenHash = url.searchParams.get('token_hash') || url.searchParams.get('token');

      try {
        if (code) {
          await supabase.auth.exchangeCodeForSession(code);
        } else if (tokenHash) {
          await supabase.auth.verifyOtp({ type: 'recovery', token_hash: tokenHash });
        } else if (hashParams.get('access_token')) {
          // O client do Supabase processa o hash automaticamente
          await new Promise((r) => setTimeout(r, 400));
        }
      } catch {
        // tratado abaixo pela checagem de sessão
      }

      const { data } = await supabase.auth.getSession();
      if (!active) return;

      if (data.session) {
        setReady(true);
      } else {
        toast({
          title: 'Link inválido ou expirado',
          description: 'Solicite um novo link de recuperação de senha.',
          variant: 'destructive',
        });
        navigate('/auth?mode=login');
      }
      setChecking(false);
    };

    prepare();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordScore(newPassword) < 4) {
      toast({
        title: 'Senha fraca',
        description: 'Cumpra pelo menos 4 dos requisitos de segurança.',
        variant: 'destructive',
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: 'Senhas não coincidem',
        description: 'Verifique se as senhas digitadas são iguais.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;

      await supabase.auth.signOut();

      toast({
        title: 'Senha redefinida!',
        description: 'Faça login com a nova senha.',
      });
      navigate('/auth?mode=login');
    } catch (error: any) {
      toast({
        title: 'Erro ao redefinir senha',
        description: error.message || 'Tente novamente',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 border-border bg-card animate-scale-in">
        <div className="flex flex-col items-center justify-center gap-3 mb-8">
          <Link to="/">
            <img src={icone} alt="Bianchim Business" className="h-16 w-auto" />
          </Link>
          <h1 className="text-xl font-bold text-foreground">Redefinir senha</h1>
          <p className="text-sm text-muted-foreground">Escolha uma nova senha de acesso</p>
        </div>

        {checking ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validando link...
          </div>
        ) : ready ? (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-foreground">Nova senha</Label>
              <PasswordInput
                id="new-password"
                value={newPassword}
                onChange={setNewPassword}
                required
                minLength={8}
                autoComplete="new-password"
                showStrength
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password" className="text-foreground">Confirmar nova senha</Label>
              <PasswordInput
                id="confirm-password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
              disabled={loading}
            >
              {loading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Redefinindo...</>
              ) : (
                'Redefinir senha'
              )}
            </Button>
          </form>
        ) : null}

        <div className="mt-6 text-center">
          <Button variant="ghost" size="sm" onClick={() => navigate('/auth?mode=login')} className="text-muted-foreground hover:text-primary">
            ← Voltar para login
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default ResetPassword;
